// src/components/modals/DropDesignationModal.js
import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { isOptionContract } from '../../services/contractUtils';
import PlayerLink from '../PlayerLink';

/**
 * DropDesignationModal
 *
 * Shown when a user tries to add a player but either:
 *   - Their roster is full (fa_add context): must designate a release
 *   - They are submitting a waiver claim (waiver_claim context): optionally
 *     designate a release in case they are awarded the claim
 *
 * All player field access uses canonical paths ONLY.
 * No flat field access. No fallback to non-canonical paths.
 * Literal defaults (|| '', || 0) are permitted.
 *
 * 9o.6.a: isOptionContract promoted to services/contractUtils.js for shared use
 * with the mobile-internal MobileDropDesignationModal. Re-exported here for
 * backward compatibility with the existing co-located test.
 */

export { isOptionContract };

const DropDesignationModal = ({
  isOpen,
  onClose,
  onConfirm,
  onRequestPlan = null,
  playerBeingAdded,
  currentRoster,
  leagueId,
  context,
  isRosterFull,
  hasActiveSlot = null,
  hasBenchSlot = null,
  waiverPriority,
  totalTeams,
  waiverExpiresAt,
}) => {
  const [selectedDropId, setSelectedDropId] = useState(null);
  const [deadMoneyCache, setDeadMoneyCache] = useState({});
  const [deadMoneyLoading, setDeadMoneyLoading] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const handleRowSelect = async (player) => {
    const id = player.ids?.league_player;
    setSelectedDropId(id);

    // Fetch the pre-drop impact for ANY selected drop. It carries both the OP+ dead-money warning AND
    // (DD-WAIVER-DROPGUARD-0725 Unit A) the waiver-claim-cancellation warning — a drop leg that removes a
    // player who is the designated drop on a pending claim would void that claim. Not OP-gated: any player
    // can be a claim's drop target.
    if (!deadMoneyCache[id]) {
      setDeadMoneyLoading(id);
      try {
        const result = await leaguesAPI.getDropImpact(leagueId, id);
        setDeadMoneyCache(prev => ({ ...prev, [id]: result }));
      } catch (err) {
        // Store a sentinel so we don't re-fetch on error
        setDeadMoneyCache(prev => ({ ...prev, [id]: { error: true } }));
      } finally {
        setDeadMoneyLoading(null);
      }
    }
  };

  const handleConfirmClick = () => {
    if (!confirming) {
      setConfirming(true);
    } else {
      onConfirm(selectedDropId);
      setConfirming(false);
    }
  };

  const handleClose = () => {
    setConfirming(false);
    onClose();
  };

  const isConfirmDisabled =
    deadMoneyLoading !== null ||
    (selectedDropId === null && isRosterFull);

  // DEFECT 2 Phase 1 — trade_placement context: same UX as waiver_claim
  // (designate a drop OR opt for an open slot when available), with
  // trade-flavored copy.
  const header =
    context === 'waiver_claim' ? 'Submit Waiver Claim'
    : context === 'trade_placement' ? 'Place Incoming Player'
    : 'Your roster is full';

  const dropListLabel =
    context === 'waiver_claim' || context === 'trade_placement'
      ? 'Designate a player to release:'
      : 'Select a player to release:';

  const confirmLabel = confirming
    ? 'Are you sure?'
    : context === 'waiver_claim'
    ? 'Submit Waiver Claim'
    : context === 'trade_placement'
    ? 'Confirm Placement'
    : 'Confirm — Add & Release';

  const showNoDropOption =
    (context === 'waiver_claim' || context === 'trade_placement') && isRosterFull === false;
  const noDropLabel =
    hasActiveSlot === false && hasBenchSlot === true
      ? 'I have an open bench spot (no drop needed — lands on bench)'
      : 'I have an open roster spot (no drop needed)';
  const rosterFullHint =
    (context === 'waiver_claim' || context === 'trade_placement') && isRosterFull === true
      ? 'Active and bench are full — this needs a roster move.'
      : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={handleClose}
      />

      {/* Modal panel */}
      <div
        className={`relative w-full max-w-lg rounded-xl shadow-2xl ${dynastyTheme.classes.bg.dark} border ${dynastyTheme.classes.border.subtle} flex flex-col max-h-[90vh]`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${dynastyTheme.classes.border.subtle}`}>
          <h2 className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
            {header}
          </h2>
          <button
            onClick={handleClose}
            className={`${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} transition-colors`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

          {/* Player being added card */}
          {playerBeingAdded && (
            <div className={`rounded-lg p-3 ${dynastyTheme.classes.bg.neutralDark} border ${dynastyTheme.classes.border.subtle}`}>
              <p className={`text-xs font-medium ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                Adding
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <PlayerLink mlbId={playerBeingAdded.ids?.mlb} name={playerBeingAdded.info?.full_name || ''} style={{ fontWeight: 600, color: '#f9fafb' }} />
                  <span className={`ml-2 text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                    {playerBeingAdded.info?.position || ''}
                    {playerBeingAdded.info?.position && playerBeingAdded.info?.mlb_team ? ' · ' : ''}
                    {playerBeingAdded.info?.mlb_team || ''}
                  </span>
                </div>
                <span className={`text-sm font-medium ${dynastyTheme.classes.text.primary}`}>
                  {playerBeingAdded.league_context?.financial?.baseline_price != null
                    ? `$${Math.round(playerBeingAdded.league_context.financial.baseline_price)}`
                    : ''}
                </span>
              </div>
            </div>
          )}

          {/* Drop list label. When an open slot exists (showNoDropOption, i.e.
              capacity says requiresDrop=false), the release is OPTIONAL — the
              label must not command a designation. */}
          <p className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight}`}>
            {(showNoDropOption && context === 'trade_placement') ? 'Optional: release a player to make room' : dropListLabel}
          </p>

          {/* Roster-full hint (waiver_claim, no active or bench slot) */}
          {rosterFullHint && (
            <p
              data-testid="roster-full-hint"
              className="text-xs text-amber-400 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30"
            >
              {rosterFullHint}
            </p>
          )}

          {/* Roster player rows.
              Filter out former players — the canonical /my-roster + /teams/{id}/roster
              endpoints (player_service.build_roster_response) bundle former players
              with current roster for full-attribution views. The drop-designation
              contract is "select a CURRENT-roster player to release"; including
              former players caused waiver claims to fail backend validation
              (drop_player.team_id != team_id at waiver_service.py:388 + roster_service.py:240).
              CANONICAL MARKER: player.info?.is_historical (NESTED under .info per
              accrued_stats_repository.py:994). NOT top-level p.is_historical. */}
          <div className="space-y-1">
            {/* No-drop option FIRST when an open slot exists — honors capacity's
                requiresDrop=false: this radio is selected by default, so the owner
                can Confirm Placement without designating a release. Previously it
                rendered LAST (below the full roster list), so it was buried. */}
            {showNoDropOption && (
              <label
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors
                  ${selectedDropId === null
                    ? `${dynastyTheme.classes.bg.primarySubtle} border ${dynastyTheme.classes.border.primary}`
                    : `${dynastyTheme.classes.bg.neutralDark} border ${dynastyTheme.classes.border.subtle} hover:border-gray-500`
                  }`}
              >
                <input
                  type="radio"
                  name="drop-selection"
                  value="__none__"
                  checked={selectedDropId === null}
                  onChange={() => setSelectedDropId(null)}
                  className="accent-current flex-shrink-0"
                />
                <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  {noDropLabel}
                </span>
              </label>
            )}

            {(currentRoster || []).filter(p => !p.info?.is_historical).map(player => {
              const id = player.ids?.league_player;
              const isSelected = selectedDropId === id;
              const contractType = player.league_context?.financial?.contract_type || '';
              const showBadge = isOptionContract(contractType);
              const cachedImpact = deadMoneyCache[id];
              const isLoadingThis = deadMoneyLoading === id;

              return (
                <div key={id}>
                  <label
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors
                      ${isSelected
                        ? `${dynastyTheme.classes.bg.primarySubtle} border ${dynastyTheme.classes.border.primary}`
                        : `${dynastyTheme.classes.bg.neutralDark} border ${dynastyTheme.classes.border.subtle} hover:border-gray-500`
                      }`}
                  >
                    <input
                      type="radio"
                      name="drop-selection"
                      value={id}
                      checked={isSelected}
                      onChange={() => handleRowSelect(player)}
                      className="accent-current flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PlayerLink mlbId={player.ids?.mlb} name={player.info?.full_name || ''} style={{ fontSize: '0.875rem', fontWeight: 500, color: '#f9fafb' }} />
                        <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                          {player.info?.position || ''}
                        </span>
                        {showBadge && (
                          <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                            {contractType}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {player.league_context?.financial?.contract_salary != null && (
                        <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                          ${player.league_context.financial.contract_salary}
                        </span>
                      )}
                      {player.league_context?.financial?.contract_years != null && (
                        <span className={`ml-1 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                          {player.league_context.financial.contract_years}yr
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Dead money warning */}
                  {isSelected && isLoadingThis && (
                    <div className="mt-1 mx-3 px-3 py-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                      <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                        Checking contract impact...
                      </span>
                    </div>
                  )}
                  {isSelected && !isLoadingThis && cachedImpact && cachedImpact.has_dead_money && cachedImpact.warning_message && (
                    <div className="mt-1 mx-3 px-3 py-2 rounded bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-yellow-300">
                        {cachedImpact.warning_message}
                      </span>
                    </div>
                  )}
                  {/* DD-WAIVER-DROPGUARD-0725 Unit A: dropping a pending claim's designated drop voids it. */}
                  {isSelected && !isLoadingThis && cachedImpact && cachedImpact.waiver_claim_warning && (
                    <div className="mt-1 mx-3 px-3 py-2 rounded bg-orange-500/10 border border-orange-500/40 flex items-start gap-2" data-testid="waiver-claim-drop-warning">
                      <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-orange-300">
                        {cachedImpact.waiver_claim_warning}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Waiver info (waiver_claim only) */}
          {context === 'waiver_claim' && (
            <div className={`rounded-lg p-3 ${dynastyTheme.classes.bg.neutralDarker} border ${dynastyTheme.classes.border.subtle} space-y-1`}>
              {waiverPriority != null && totalTeams != null && (
                <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                  Your waiver priority: <span className={dynastyTheme.classes.text.white}>#{waiverPriority} of {totalTeams}</span>
                </p>
              )}
              {waiverExpiresAt && (
                <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                  Claims process:{' '}
                  <span className={dynastyTheme.classes.text.white}>
                    {new Date(waiverExpiresAt).toLocaleString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${dynastyTheme.classes.border.subtle}`}>
          <button
            onClick={handleClose}
            className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
          >
            Cancel
          </button>
          {context === 'waiver_claim' && hasActiveSlot === false && onRequestPlan && (
            <button
              onClick={onRequestPlan}
              data-testid="use-plan-button"
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              Rearrange roster to put in active {playerBeingAdded?.info?.position || ''}
            </button>
          )}
          <button
            onClick={handleConfirmClick}
            disabled={isConfirmDisabled}
            className={`${dynastyTheme.utils.getComponent('button', confirming ? 'danger' : 'primary', 'md')} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirming ? 'Are you sure?' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DropDesignationModal;
